import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router";
import { getCurrentUser, GoogleUser } from "./firebase/auth";
import {
  fetchByWishlist,
  fetchReserved,
  storeReserved,
  storeUnreserved,
} from "./firestore/reserved";
import { fetchFriends } from "./firestore/friends";
import { RootState } from "./redux/store";
import { fetchProfile, Profile } from "./firestore/profile";
import {
  Id,
  Reserved,
  Wishlist,
  WishlistItem,
  NotificationView,
} from "./types";
import { loadFriends, loadNotifications } from "./redux/user";
import {
  fetchMultipleWishlists,
  fetchSingleWishlist,
  loadOrCreateWishlists,
  updateWish,
  deleteWish,
} from "./firestore/wishlist";
import { loadData, setIsLoading } from "./redux/wishlist";
import { getNotifications } from "./firebase/notifications";
import {
  selectDefaultWishlistId,
  selectIsLoadingWishlist,
  selectMultipleWishlists,
  selectWishlist,
} from "./redux/wishlist/selectors";
import { selectFriends, selectNotifications } from "./redux/user/selectors";

export function useCurrentRoute(): string {
  const { pathname } = useLocation();
  const route = /\/wishlist\/\w+/.test(pathname)
    ? "/wishlist/:wishlistId"
    : pathname;

  return route;
}

const MAX_RETRIES = 3;

export function useLoadMultipleWishlists(ids: Id[]): Wishlist[] | undefined {
  const wishlists = useSelector(selectMultipleWishlists(ids));
  const dispatch = useDispatch();
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    const loadedIds: Id[] = wishlists?.map((w) => w.id) || [];
    const missingIds = ids.filter((i) => !loadedIds.includes(i));
    if (missingIds.length == 0 || counter > MAX_RETRIES) {
      return;
    }
    setCounter((c) => c + 1);

    dispatch(setIsLoading());
    fetchMultipleWishlists(missingIds)
      .then((newWishlists) => {
        dispatch(loadData({ wishlists: newWishlists || [] }));
      })
      .catch(console.error);
  }, [ids, wishlists, counter]);

  return wishlists;
}

export function useLoadWishlist(): [Wishlist | undefined, boolean] {
  const [isLoaded, setIsLoaded] = useState(false);
  const { wishlistId } = useParams();
  const wishlist = useSelector(selectWishlist(wishlistId));
  const dispatch = useDispatch();

  useEffect(() => {
    if (!wishlist && wishlistId) {
      const fetchData = async () => {
        dispatch(setIsLoading());
        const wishlist = await fetchSingleWishlist(wishlistId);
        if (!wishlist) {
          return;
        }
        dispatch(loadData({ wishlists: [wishlist] }));
      };

      fetchData()
        .catch(console.error)
        .finally(() => setIsLoaded(true));
    }
  }, [wishlistId, wishlist]);

  if (!wishlistId) {
    return [undefined, isLoaded];
  }

  return [wishlist, isLoaded];
}

export type WishModifier = (wish: WishlistItem) => Promise<void>;
export type ReservedModifier = (r: Reserved) => Promise<void>;

export function useWishlist(): {
  wishlist?: Wishlist;
  isLoaded: boolean;
  updateWish?: WishModifier;
  deleteWish?: WishModifier;
  reserved?: Reserved[];
  reserve?: ReservedModifier;
  unreserve?: ReservedModifier;
} {
  const [isLoaded, setIsLoaded] = useState(false);
  const { wishlistId } = useParams();
  const wishlist = useSelector(selectWishlist(wishlistId));
  const isLoading = useSelector(selectIsLoadingWishlist);
  const dispatch = useDispatch();
  const { user, authStateLoaded } = useCurrentUser();
  const [reserved, setReserved] = useState<Reserved[] | undefined>(undefined);

  useEffect(() => {
    setIsLoaded(!isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!wishlist && wishlistId) {
      const fetchData = async () => {
        dispatch(setIsLoading());
        const wishlist = await fetchSingleWishlist(wishlistId);
        if (!wishlist) {
          return;
        }
        dispatch(loadData({ wishlists: [wishlist] }));
      };

      fetchData()
        .catch((e) => console.error("useWishlist", e))
        .finally(() => setIsLoaded(true));
    } else if (wishlist && !isLoaded) {
      setIsLoaded(true);
    }
  }, [wishlistId, wishlist, isLoaded]);

  useEffect(() => {
    if (!reserved && wishlistId) {
      fetchByWishlist(wishlistId)
        .then((data) => setReserved(data))
        .catch((e) => console.error("useWishlist > fetchByWishlist", e));
    }
  }, [reserved, wishlistId]);

  if (!wishlistId) {
    return { isLoaded: false };
  }

  if (!authStateLoaded) {
    return { wishlist, isLoaded, reserved };
  }

  const updateWishFun = async (wish: WishlistItem) => {
    setIsLoaded(false);
    dispatch(setIsLoading());
    const newWishlist = await updateWish(wishlistId, wish);
    dispatch(loadData({ wishlists: [newWishlist] }));
    setIsLoaded(true);
  };

  const deleteWishFun = async (wish: WishlistItem) => {
    setIsLoaded(false);
    dispatch(setIsLoading());
    const newWishlist = await deleteWish(wishlistId, wish.id);
    dispatch(loadData({ wishlists: [newWishlist] }));
    setIsLoaded(true);
  };

  const reserveFun = async (r: Reserved) => {
    setIsLoaded(false);
    await storeReserved(r.userId, r.wishlistId, r.wishId);
    setReserved(undefined);
    setIsLoaded(true);
  };

  const unreserveFun = async (r: Reserved) => {
    setIsLoaded(false);
    await storeUnreserved(r.userId, r.wishlistId, r.wishId);
    setReserved(undefined);
    setIsLoaded(true);
  };

  const isMyWishlist = user?.uid === wishlist?.userId;
  if (isMyWishlist) {
    return {
      wishlist,
      isLoaded,
      updateWish: updateWishFun,
      deleteWish: deleteWishFun,
      reserved,
    };
  }

  return {
    wishlist,
    isLoaded,
    reserved,
    reserve: reserveFun,
    unreserve: unreserveFun,
  };
}

export function useLoadMyDefaultWishlistId(): Id | undefined {
  const user = getCurrentUser();
  const defaultWishlistId = useSelector(selectDefaultWishlistId);
  const dispatch = useDispatch();

  useEffect(() => {
    if (user?.uid && !defaultWishlistId) {
      dispatch(setIsLoading());
      loadOrCreateWishlists(user.uid)
        .then((wishlists) => dispatch(loadData({ wishlists })))
        .catch(console.error);
    }
  }, [user?.uid]);

  if (!user?.uid) {
    return undefined;
  }

  return defaultWishlistId;
}

interface CurrentUser {
  user?: GoogleUser;
  displayName: string;
  description: string;
  authStateLoaded: boolean;
  profileLoaded: boolean;
}

export function useProfile(userId: Id | undefined): Profile | undefined {
  const [uid, setUid] = useState<Id | undefined>(undefined);
  const [profile, setProfile] = useState<Profile | undefined>(undefined);
  if (uid !== userId) {
    setUid(userId);
    setProfile(undefined);
  }

  useEffect(() => {
    if (uid && typeof profile == "undefined") {
      fetchProfile(uid).then((newProfile) => {
        setProfile(newProfile);
      });
    }
  }, [uid, profile]);

  return profile;
}

export function useCurrentUser(): CurrentUser {
  const user = getCurrentUser();
  const profile = useProfile(user?.uid);
  const authStateLoaded = useSelector(
    (state: RootState) => state.user.authStateLoaded,
  );
  if (!user) {
    return {
      user: user || undefined,
      displayName: "",
      description: "",
      authStateLoaded,
      profileLoaded: typeof profile !== "undefined",
    };
  }

  return {
    user,
    displayName: profile?.displayName || "",
    description: profile?.description || "",
    authStateLoaded,
    profileLoaded: typeof profile !== "undefined",
  };
}

export function useIsMyWishlist(): boolean {
  const [wishlist] = useLoadWishlist();
  const { user } = useCurrentUser();

  return wishlist?.userId == user?.uid;
}

export function useFriends(): Id[] | undefined {
  const { user } = useCurrentUser();
  const friends = useSelector(selectFriends);
  const dispatch = useDispatch();

  useEffect(() => {
    if (user?.uid && typeof friends === "undefined") {
      fetchFriends(user.uid)
        .then(({ friendIds }) => dispatch(loadFriends(friendIds)))
        .catch(console.error);
    }
  }, [user?.uid, friends]);

  if (!user?.uid) {
    return undefined;
  }

  return friends;
}

interface UseNotifications {
  isLoaded: boolean;
  notifications?: NotificationView[];
}
export function useNotifications(): UseNotifications {
  const { user } = useCurrentUser();
  const [isLoaded, setIsLoaded] = useState(false);
  const notifications = useSelector(selectNotifications);
  const dispatch = useDispatch();

  useEffect(() => {
    if (user?.uid && typeof notifications === "undefined") {
      getNotifications()
        .then((nots) => {
          dispatch(loadNotifications(nots));
          setIsLoaded(true);
        })
        .catch((e) => {
          setIsLoaded(true);
          console.error(e);
        });
    }

    if (typeof notifications !== "undefined") {
      setIsLoaded(true);
    }
  }, [user?.uid, notifications]);

  if (!user?.uid) {
    return { isLoaded: false };
  }

  return { isLoaded, notifications };
}

type ToggleReserved = (wishlistId: Id, wishId: Id) => Promise<Reserved[]>;

interface UseReserved {
  isLoaded: boolean;
  reserved: Reserved[];
  reserve: ToggleReserved;
  unreserve: ToggleReserved;
}

export function useReserved(): UseReserved {
  const { user } = useCurrentUser();
  const [isLoaded, setIsLoaded] = useState(false);
  const [reserved, setReserved] = useState<Reserved[] | undefined>(undefined);

  useEffect(() => {
    if (typeof reserved === "undefined" && user?.uid) {
      fetchReserved(user?.uid).then((newReserved) => {
        setReserved(newReserved);
        setIsLoaded(true);
      });
    }
  }, [reserved, user?.uid]);

  const reserve: ToggleReserved = async (
    wishlistId: Id,
    wishId: Id,
  ): Promise<Reserved[]> => {
    if (!user) {
      throw new Error("Not logged in");
    }
    setIsLoaded(false);
    const newReserved = await storeReserved(user?.uid, wishlistId, wishId);
    setReserved(newReserved);
    setIsLoaded(true);

    return newReserved;
  };

  const unreserve: ToggleReserved = async (
    wishlistId: Id,
    wishId: Id,
  ): Promise<Reserved[]> => {
    if (!user) {
      throw new Error("Not logged in");
    }
    setIsLoaded(false);
    const newReserved = await storeUnreserved(user?.uid, wishlistId, wishId);
    setReserved(newReserved);
    setIsLoaded(true);

    return newReserved;
  };

  return { isLoaded, reserved: reserved || [], reserve, unreserve };
}
